import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TableSortLabel,
  Tooltip,
  Checkbox,
  Button,
  Box, Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import QrCodeIcon from "@mui/icons-material/QrCode";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { useNavigate } from "react-router-dom";
import DeleteConfirmation from "../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import { DeleteBottleOrder } from "../../services/BottleOrdersService";
import { PrintBottleOrderLabels, GenerateBigBagLabel, GenerateBoxLabelPrint } from "../../../services/barcodeService";
import {
  getComparator,
  stableSort,
} from "../../../common-utilities/sortingUtils";
import BatchUpdateBottleOrders from "./BatchUpdateBottleOrders";
import BatchDuplicateBottleOrders from "./BatchDuplicateBottleOrders";

// bottleOrdersData: rows for the CURRENT page only (server-paginated)
// count: TOTAL matching rows across all pages (from the API)
// onChange(page, pageSize): asks the parent to fetch a different page/pageSize
const BottleOrdersTable = ({ bottleOrdersData, count, onChange }) => {
  const columns = [
    "", // checkbox
    "Order Number",
    "Name",
    "Description",
    "Ship By",
    "Firm",
    "Kit Due",
    "Due On",
    "Shipped",
    "Status",
    "Actions",
  ];
  const columnHeaders = [
    "orderNumber",
    "shippingName",
    "description",
    "shipBy",
    "shippingDueDateFirm",
    "kitPrepDueDate",
    "dueDate",
    "shippingDate",
    "shippingStatus",
  ];

  const navigate = useNavigate();
  const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [selectedOrderToDelete, setSelectedOrderToDelete] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [tableHeight, setTableHeight] = useState(600);

  // Batch selection state
  const [selectedRows, setSelectedRows] = useState([]);
  const [isBatchUpdateOpen, setIsBatchUpdateOpen] = useState(false);
  const [isBatchDuplicateOpen, setIsBatchDuplicateOpen] = useState(false);

  const tableContainerRef = useRef(null);
  const [orders, setOrders] = useState([]);

  // Fetch a new page whenever page/rowsPerPage changes - same pattern as LabOrdersTable
  useEffect(() => {
    handleChange();
  }, [page, rowsPerPage]);

  const handleChange = () => {
    onChange(page, rowsPerPage);
  };

  useEffect(() => {
    setOrders(bottleOrdersData || []);
  }, [bottleOrdersData]);

  useEffect(() => {
    const calculateTableHeight = () => {
      if (tableContainerRef.current) {
        const containerTop = tableContainerRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        // Reserve space for pagination (56px) and bottom padding (20px)
        const availableHeight = windowHeight - containerTop - 76;
        const calculatedHeight = Math.max(300, Math.min(availableHeight, windowHeight - 200));
        setTableHeight(calculatedHeight);
      }
    };

    calculateTableHeight();
    window.addEventListener("resize", calculateTableHeight);
    const timeoutId = setTimeout(calculateTableHeight, 100);

    return () => {
      window.removeEventListener("resize", calculateTableHeight);
      clearTimeout(timeoutId);
    };
  }, [bottleOrdersData, selectedRows]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleDeleteClick = (orderNumber) => {
    setSelectedOrderToDelete(orderNumber);
    setIsDeleteConfirmationOpen(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleDeleteConfirmationClose = () => setIsDeleteConfirmationOpen(false);

  const handleBottleLabelsPrint = async (orderNumber) => {
    try {
      await PrintBottleOrderLabels("sampleLabel", orderNumber);
      NotificationManager.success("Bottle order labels printed successfully");
    } catch (error) {
      NotificationManager.error("Error printing bottle order");
    }
  };

  const renderWithTooltip = (text) => {
    if (!text) return "-";
    const words = text.split(" ");
    const isLong = words.length > 3;
    const shortText = isLong ? words.slice(0, 3).join(" ") + "..." : text;
    return (
      <Tooltip title={text}>
        <span style={{ cursor: "pointer" }}>{shortText}</span>
      </Tooltip>
    );
  };

  const handleBottleBarCodePrint = async (orderNumber) => {
    try {
      await PrintBottleOrderLabels("sampleBarcode", orderNumber);
      NotificationManager.success("Bottle order labels printed successfully");
    } catch (error) {
      NotificationManager.error("Error printing bottle order");
    }
  };

  const handleBigBagLabelPrint = async (orderNumber) => {
    try {
      await GenerateBigBagLabel("bottleOrderBigBagLabel", orderNumber);
      NotificationManager.success("Big bag label printed successfully");
    } catch (error) {
      // GenerateBigBagLabel already shows the stage-specific NotificationManager
      // error (Label API / Print Service / Printer Mapping / Printer) - avoid
      // a second, less specific toast for the same failure.
    }
  };

  const handleBoxLabelPrint = async (orderNumber) => {
    try {
      await GenerateBoxLabelPrint("orderedBottleBoxLabel", orderNumber);
      NotificationManager.success("Box label printed successfully");
    } catch (error) {
      // GenerateBoxLabelPrint already shows the stage-specific NotificationManager
      // error (Label API / Print Service / Printer Mapping / Printer) - avoid
      // a second, less specific toast for the same failure.
    }
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteBottleOrder(selectedOrderToDelete);
      if (response.results === true) {
        NotificationManager.success("Bottle order deleted successfully");
        setIsDeleteConfirmationOpen(false);
        setSelectedRows((prev) => prev.filter((id) => id !== selectedOrderToDelete));
        // Data is server-paginated now, so re-fetch the current page instead of
        // just filtering the row out of local state (count/page contents would drift).
        onChange(page, rowsPerPage);
      } else {
        NotificationManager.error("Error deleting bottle order");
      }
    } catch (error) {
      NotificationManager.error("Error deleting bottle order");
    }
  };

  // orders is already just the current page's rows (server-paginated) -
  // sort only reorders what's visible on this page, same behavior as LabOrdersTable.
  const sortedData = stableSort(orders, getComparator(order, orderBy));

  const isAllSelected =
    sortedData.length > 0 &&
    sortedData.every((o) => selectedRows.includes(o.orderNumber));

  const isIndeterminate = selectedRows.length > 0 && !isAllSelected;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allOnPage = sortedData.map((o) => o.orderNumber);
      setSelectedRows((prev) => [...new Set([...prev, ...allOnPage])]);
    } else {
      const allOnPage = new Set(sortedData.map((o) => o.orderNumber));
      setSelectedRows((prev) => prev.filter((id) => !allOnPage.has(id)));
    }
  };

  const handleRowSelect = (orderNumber) => {
    setSelectedRows((prev) =>
      prev.includes(orderNumber)
        ? prev.filter((id) => id !== orderNumber)
        : [...prev, orderNumber]
    );
  };

  // Note: selectedOrders only resolves rows that are on the CURRENTLY loaded page.
  // If batch update/duplicate needs to work across pages, selectedRows would need
  // to carry the full order objects (or be looked up) rather than just IDs.
  const selectedOrders = orders.filter((o) => selectedRows.includes(o.orderNumber));

  const renderActions = (orderNumber) => (
    <div style={{ minWidth: "80px", display: "flex", gap: "4px" }}>
      <Tooltip title="Print Bottle Labels">
        <IconButton
          aria-label="print bottle labels"
          style={{ color: "#0eecf0" }}
          onClick={() => handleBottleLabelsPrint(orderNumber)}
        >
          <LocalPrintshopIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Print Barcode Labels">
        <IconButton
          aria-label="print barcode"
          style={{ color: "#8911f2" }}
          onClick={() => handleBottleBarCodePrint(orderNumber)}
        >
          <QrCodeIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Print Box Label">
        <IconButton
          aria-label="print box label"
          style={{ color: "#ff9800" }}
          onClick={() => handleBoxLabelPrint(orderNumber)}
        >
          <LocalShippingIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Print Big Bag Label">
        <IconButton
          aria-label="print big bag label"
          style={{ color: "#795548" }}
          onClick={() => handleBigBagLabelPrint(orderNumber)}
        >
          <Inventory2Icon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Delete Order">
        <IconButton
          aria-label="delete"
          style={{ color: "red" }}
          onClick={() => handleDeleteClick(orderNumber)}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <>
      {selectedRows.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            p: 1,
            backgroundColor: "primary.lighter",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "primary.light",
          }}
        >
          <span style={{ fontSize: "0.875rem", color: "#1976d2", fontWeight: 500 }}>
            {selectedRows.length} order(s) selected
          </span>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<EditNoteIcon />}
            onClick={() => setIsBatchUpdateOpen(true)}
          >
            Update
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={() => setIsBatchDuplicateOpen(true)}
          >
            Duplicate
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => setSelectedRows([])}
          >
            Clear
          </Button>
        </Box>
      )}

      <Paper>
        <TableContainer
          ref={tableContainerRef}
          sx={{ maxHeight: tableHeight, overflowY: "auto", width: "97%" }}
        >
          <Table sx={{ minWidth: 700 }} size="small" stickyHeader>
            <TableHead
              sx={{
                backgroundColor: (theme) => theme.palette.primary.main,
                color: "white",
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    style={{ color: "white" }}
                  />
                </TableCell>
                {columns.slice(1).map((column, index) => (
                  <TableCell
                    key={column}
                    onClick={() => handleRequestSort(columnHeaders[index])}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableSortLabel
                      active={orderBy === columnHeaders[index]}
                      direction={
                        orderBy === columnHeaders[index] ? order : "asc"
                      }
                    >
                      {column}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row, index) => (
                <TableRow
                  key={row.orderNumber}
                  selected={selectedRows.includes(row.orderNumber)}
                  sx={{
                    backgroundColor:
                      selectedRows.includes(row.orderNumber)
                        ? "rgba(25, 118, 210, 0.08)"
                        : index % 2 === 0
                          ? "rgba(0, 0, 0, 0.04)"
                          : "white",
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRows.includes(row.orderNumber)}
                      onChange={() => handleRowSelect(row.orderNumber)}
                    />
                  </TableCell>
                  {columnHeaders.map((column) => (
                    <TableCell key={column}>
                      {column === "kitPrepDueDate" ||
                        column === "dueDate" ||
                        column === "shippingDate" ? (
                        formatDate(row[column])
                      ) : column === "shippingDueDateFirm" ? (
                        <Chip
                          label={row[column] ? "Yes" : "Flexible"}
                          size="small"
                          sx={{
                            backgroundColor: row[column] ? "#f44336" : "#4caf50",
                            color: "white",
                            fontWeight: 600,
                            minWidth: "80px",
                          }}
                        />
                      ) : column === "orderNumber" ? (
                        row[column] ? (
                          <a
                            href={`/bottle-orders/edit/${row[column]}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#1976d2",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            {row[column]}
                          </a>
                        ) : (
                          "-"
                        )
                      ) : column === "shippingName" ||
                        column === "description" ? (
                        renderWithTooltip(row[column])
                      ) : (
                        row[column]
                      )}
                    </TableCell>
                  ))}
                  <TableCell>{renderActions(row.orderNumber)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this bottle order?"
        color="red"
      />

      <BatchUpdateBottleOrders
        open={isBatchUpdateOpen}
        onClose={() => setIsBatchUpdateOpen(false)}
        selectedOrders={selectedOrders}
        onSuccess={() => {
          setSelectedRows([]);
          setIsBatchUpdateOpen(false);
          onChange(page, rowsPerPage);
        }}
      />

      <BatchDuplicateBottleOrders
        open={isBatchDuplicateOpen}
        onClose={() => {
          setIsBatchDuplicateOpen(false);
          setSelectedRows([]);
        }}
        selectedOrders={selectedOrders}
        onSuccess={() => { }}
      />
    </>
  );
};

export default BottleOrdersTable;